import {canAccess} from "./access/policy";
import {watchPatientAccounts} from "./access/watchPatientAccounts";
import {agendaDirectory} from "./access/patientDirectory";
import {patientDirectory} from "./access/patientDirectory";

import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
} from "recharts";
import { useEffect, useState } from "react";
import { db } from "./firebase";
import { useClinic } from "./context/ClinicContext";
import {
  getDateKeyInTimeZone,
  getDeviceTimeZone,
  localizeScheduleEntry,
} from "./utils/timezone";
import {
  collection,
  onSnapshot,
} from "./access/firestore";

const colores = {
  azul: "#2563eb",
  azulOscuro: "#1d4ed8",
  fondo: "#f4f7fc",
  texto: "#172033",
  secundario: "#71809a",
  borde: "#e7edf5",
  verde: "#16a34a",
  naranja: "#f59e0b",
  morado: "#9333ea",
};

const iconos = {
  citas: "▦",
  ingresos: "$",
  pacientes: "♙",
  whatsapp: "◉",
  saldo: "⊕",
  calendario: "▣",
  actividad: "↗",
};

const formatoDinero = (cantidad) =>
  `$${Number(cantidad || 0).toLocaleString("es-MX", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  })}`;

function TarjetaIndicador({
  titulo,
  valor,
  descripcion,
  icono,
  color,
  fondoIcono,
}) {
  return (
    <div className="vd-stat">
      <div className="vd-stat-top">
        <div
          className="vd-stat-icon"
          style={{
            color,
            background: fondoIcono,
          }}
        >
          {icono}
        </div>
        <span className="vd-stat-title">{titulo}</span>
      </div>

      <div className="vd-stat-value">{valor}</div>

      <div className="vd-stat-description">{descripcion}</div>
    </div>
  );
}

function Panel({ titulo, subtitulo, children, className = "" }) {
  return (
    <section className={`vd-panel ${className}`}>
      <div className="vd-panel-heading">
        <div>
          <h2>{titulo}</h2>
          {subtitulo && <p>{subtitulo}</p>}
        </div>
      </div>
      {children}
    </section>
  );
}

export default function Dashboard() {
  const { clinicId, currentUser } = useClinic();
  const deviceTimeZone = getDeviceTimeZone();

  const [citas, setCitas] = useState([]);
  const [pagos, setPagos] = useState([]);
  const [pacientes, setPacientes] = useState([]);
  const [cuentasPorCobrar, setCuentasPorCobrar] = useState(null);
  const [conversations, setConversations] = useState([]);

  const readAppointments=canAccess(currentUser,"appointments"), readFinance=canAccess(currentUser,"finance"), readPatients=canAccess(currentUser,"patients"), readWhatsApp=canAccess(currentUser,"whatsapp");
  useEffect(() => {
    setCitas([]); setPagos([]); setPacientes([]); setConversations([]); setCuentasPorCobrar([]);
    if(!clinicId) return;
    let active=true;
    const subscriptions=[];
    const listen=(reference,setter,mapper=(document)=>({id:document.id,...document.data()}))=>subscriptions.push(onSnapshot(reference,snapshot=>{if(active)setter(snapshot.docs.map(mapper));},caught=>{if(active)console.error("No se pudo cargar un indicador del panel",caught.code);}));
    if(readAppointments)listen(agendaDirectory(db,clinicId),setCitas,document=>localizeScheduleEntry({id:document.id,...document.data()},deviceTimeZone));
    if(readFinance)listen(collection(db,"clinics",clinicId,"pagos"),setPagos);
    if(readPatients)listen(patientDirectory(db,clinicId),setPacientes);
    if(readWhatsApp)listen(collection(db,"clinics",clinicId,"whatsappConversations"),setConversations);
    if(readFinance && readPatients)subscriptions.push(watchPatientAccounts(db,clinicId,accounts=>{if(active)setCuentasPorCobrar(accounts);},caught=>{if(active)console.error("No se pudieron cargar las cuentas del consultorio",caught.code);}));
    return()=>{active=false;subscriptions.forEach(stop=>stop());};
  },[clinicId,currentUser,readAppointments,readFinance,readPatients,readWhatsApp,deviceTimeZone]);

  // Se conservan los cálculos del panel original.
  const hoy = getDateKeyInTimeZone(new Date(), deviceTimeZone);
  const mes = new Date().getMonth();
  const anio = new Date().getFullYear();

  const citasHoy = citas
    .filter((c) => c.fecha === hoy)
    .sort((a, b) =>
      (a.hora || "").localeCompare(b.hora || "")
    );

  const ahora = new Date();

  const horaActual = `${String(ahora.getHours()).padStart(
    2,
    "0"
  )}:${String(ahora.getMinutes()).padStart(2, "0")}`;

  const proximaCita = citasHoy.find(
    (c) => (c.hora || "00:00") >= horaActual
  );

  const ingresosHoy = pagos.reduce((acc, p) => {
    const fecha = p.fecha?.seconds
      ? new Date(p.fecha.seconds * 1000)
          .toISOString()
          .split("T")[0]
      : null;

    return fecha === hoy ? acc + Number(p.monto || 0) : acc;
  }, 0);

  const ingresosMes = pagos.reduce((acc, p) => {
    if (!p.fecha?.seconds) return acc;

    const d = new Date(p.fecha.seconds * 1000);

    return d.getMonth() === mes && d.getFullYear() === anio
      ? acc + Number(p.monto || 0)
      : acc;
  }, 0);

  const saldoPendiente = (cuentasPorCobrar ?? []).reduce(
    (total, cuenta) => total + Number(cuenta.saldo || 0),
    0
  );

  const pacientesMes = pacientes.filter((p) => {
    if (!p.createdAt?.seconds) return false;

    const d = new Date(p.createdAt.seconds * 1000);

    return d.getMonth() === mes && d.getFullYear() === anio;
  }).length;

  const whatsappUnread = conversations.reduce(
    (acc, c) => acc + Number(c.unreadCount || 0),
    0
  );

  const saludo = (() => {
    const hora = new Date().getHours();

    if (hora < 12) return "Buenos días";
    if (hora < 19) return "Buenas tardes";
    return "Buenas noches";
  })();

  const ingresos7Dias = [];

  for (let i = 6; i >= 0; i--) {
    const fecha = new Date();
    fecha.setDate(fecha.getDate() - i);

    const dia = fecha.toLocaleDateString("es-MX", {
      weekday: "short",
    });

    const total = pagos.reduce((acc, p) => {
      if (!p.fecha?.seconds) return acc;

      const d = new Date(p.fecha.seconds * 1000);

      return d.toDateString() === fecha.toDateString()
        ? acc + Number(p.monto || 0)
        : acc;
    }, 0);

    ingresos7Dias.push({ dia, total });
  }

  const pacientes30Dias = [];

  for (let i = 29; i >= 0; i--) {
    const fecha = new Date();
    fecha.setDate(fecha.getDate() - i);

    const total = pacientes.filter((p) => {
      if (!p.createdAt?.seconds) return false;

      const d = new Date(p.createdAt.seconds * 1000);

      return d.toDateString() === fecha.toDateString();
    }).length;

    pacientes30Dias.push({
      dia: fecha.getDate(),
      total,
    });
  }

  const ocupacion = [
    "Lun",
    "Mar",
    "Mié",
    "Jue",
    "Vie",
    "Sáb",
    "Dom",
  ].map((dia, index) => {
    const total = citas.filter((c) => {
      const d = new Date(c.fecha);

      return d.getDay() === (index + 1) % 7;
    }).length;

    return {
      dia,
      porcentaje: Math.min(Math.round((total / 10) * 100), 100),
    };
  });

  const fechaLarga = new Date().toLocaleDateString("es-MX", {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
  });

  return (
    <div className="vd-dashboard">
      <style>{`
        .vd-dashboard {
          box-sizing: border-box;
          width: 100%;
          min-width: 0;
          padding: 24px;
          color: ${colores.texto};
          background: ${colores.fondo};
          font-family: Inter, -apple-system, BlinkMacSystemFont,
            "Segoe UI", sans-serif;
        }

        .vd-dashboard *,
        .vd-dashboard *::before,
        .vd-dashboard *::after {
          box-sizing: border-box;
        }

        .vd-hero {
          position: relative;
          isolation: isolate;
          overflow: hidden;
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 24px;
          min-height: 164px;
          padding: 30px 34px;
          color: white;
          border-radius: 24px;
          background: linear-gradient(115deg, #3474f6 0%, #2455df 56%, #1e40af 100%);
          box-shadow: 0 14px 32px rgba(37, 99, 235, .17);
        }

        .vd-hero::before,
        .vd-hero::after {
          content: "";
          position: absolute;
          z-index: -1;
          border-radius: 50%;
          background: rgba(255,255,255,.07);
          pointer-events: none;
        }

        .vd-hero::before {
          width: 300px;
          height: 300px;
          top: -190px;
          right: 90px;
        }

        .vd-hero::after {
          width: 230px;
          height: 230px;
          right: -95px;
          bottom: -150px;
        }

        .vd-hero-content {
          min-width: 0;
        }

        .vd-hero-eyebrow {
          display: flex;
          align-items: center;
          gap: 9px;
          margin-bottom: 12px;
          color: rgba(255,255,255,.9);
          font-size: 12px;
          font-weight: 750;
          letter-spacing: 1.3px;
          text-transform: uppercase;
        }

        .vd-hero h1 {
          margin: 0;
          font-size: clamp(24px, 2.2vw, 34px);
          font-weight: 750;
          line-height: 1.2;
          letter-spacing: -.8px;
        }

        .vd-hero p {
          margin: 10px 0 0;
          color: rgba(255,255,255,.82);
          font-size: 14px;
          line-height: 1.6;
        }

        .vd-hero-date {
          flex-shrink: 0;
          min-width: 175px;
          padding: 15px 18px;
          border: 1px solid rgba(255,255,255,.22);
          border-radius: 15px;
          background: rgba(255,255,255,.10);
          text-align: center;
          backdrop-filter: blur(8px);
        }

        .vd-hero-date-label {
          font-size: 11px;
          color: rgba(255,255,255,.75);
          text-transform: uppercase;
          letter-spacing: .8px;
        }

        .vd-hero-date-value {
          margin-top: 6px;
          font-size: 13px;
          font-weight: 650;
          text-transform: capitalize;
        }

        .vd-section {
          margin-top: 26px;
        }

        .vd-section-heading {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 12px;
          margin-bottom: 14px;
        }

        .vd-section-heading h2 {
          margin: 0;
          font-size: 19px;
          font-weight: 750;
          letter-spacing: -.35px;
        }

        .vd-section-heading p {
          margin: 5px 0 0;
          color: ${colores.secundario};
          font-size: 12px;
        }

        .vd-section-tag {
          color: ${colores.secundario};
          font-size: 12px;
          font-weight: 600;
        }

        .vd-stats {
          display: grid;
          grid-template-columns: repeat(3, minmax(0, 1fr));
          gap: 15px;
        }

        .vd-stat {
          min-width: 0;
          padding: 19px;
          border: 1px solid ${colores.borde};
          border-radius: 19px;
          background: white;
          box-shadow: 0 4px 16px rgba(31, 51, 86, .035);
          transition: transform .18s ease, box-shadow .18s ease;
        }

        .vd-stat:hover {
          transform: translateY(-2px);
          box-shadow: 0 9px 24px rgba(31, 51, 86, .075);
        }

        .vd-stat-top {
          display: flex;
          align-items: center;
          gap: 11px;
          min-width: 0;
        }

        .vd-stat-icon {
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
          width: 39px;
          height: 39px;
          border-radius: 12px;
          font-size: 22px;
          font-weight: 750;
        }

        .vd-stat-title {
          color: ${colores.secundario};
          font-size: 12px;
          font-weight: 650;
          line-height: 1.4;
        }

        .vd-stat-value {
          margin-top: 17px;
          overflow-wrap: anywhere;
          font-size: clamp(23px, 2vw, 31px);
          font-weight: 780;
          letter-spacing: -.7px;
          line-height: 1.15;
        }

        .vd-stat-description {
          margin-top: 8px;
          color: ${colores.secundario};
          font-size: 11px;
          line-height: 1.5;
        }

        .vd-welcome-grid {
          display: grid;
          grid-template-columns: minmax(0, 1.5fr) minmax(260px, 1fr);
          gap: 16px;
        }

        .vd-welcome-card {
          display: flex;
          align-items: flex-start;
          gap: 16px;
          min-width: 0;
          padding: 23px;
          border: 1px solid ${colores.borde};
          border-radius: 20px;
          background: white;
          box-shadow: 0 4px 16px rgba(31, 51, 86, .035);
        }

        .vd-welcome-icon {
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
          width: 46px;
          height: 46px;
          border-radius: 14px;
          color: ${colores.azul};
          background: #eff6ff;
          font-size: 23px;
          font-weight: 700;
        }

        .vd-welcome-card h3 {
          margin: 1px 0 8px;
          font-size: 15px;
          font-weight: 750;
        }

        .vd-welcome-card p {
          margin: 0;
          color: ${colores.secundario};
          font-size: 13px;
          line-height: 1.8;
        }

        .vd-appointment-time {
          margin: 3px 0 7px;
          color: ${colores.azul};
          font-size: 29px;
          font-weight: 780;
          letter-spacing: -.6px;
        }

        .vd-appointment-name {
          color: ${colores.texto};
          font-size: 13px;
          font-weight: 650;
          overflow-wrap: anywhere;
        }

        .vd-appointment-empty {
          margin-top: 10px;
          color: ${colores.secundario};
          font-size: 13px;
        }

        .vd-charts-grid {
          display: grid;
          grid-template-columns: repeat(2, minmax(0, 1fr));
          gap: 16px;
        }

        .vd-panel {
          min-width: 0;
          padding: 22px;
          border: 1px solid ${colores.borde};
          border-radius: 20px;
          background: white;
          box-shadow: 0 4px 16px rgba(31, 51, 86, .035);
        }

        .vd-panel-heading {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 12px;
          margin-bottom: 19px;
        }

        .vd-panel-heading h2 {
          margin: 0;
          font-size: 15px;
          font-weight: 750;
          letter-spacing: -.2px;
        }

        .vd-panel-heading p {
          margin: 5px 0 0;
          color: ${colores.secundario};
          font-size: 12px;
        }

        .vd-chart {
          width: 100%;
          height: 250px;
          min-width: 0;
        }

        .vd-insights {
          display: grid;
          grid-template-columns: repeat(2, minmax(0, 1fr));
          gap: 12px;
        }

        .vd-insight {
          display: flex;
          align-items: flex-start;
          gap: 11px;
          min-width: 0;
          padding: 15px;
          border: 1px solid ${colores.borde};
          border-radius: 14px;
          background: #fbfcff;
          color: #475569;
          font-size: 12px;
          line-height: 1.6;
        }

        .vd-insight-dot {
          flex-shrink: 0;
          width: 9px;
          height: 9px;
          margin-top: 5px;
          border-radius: 50%;
          background: ${colores.azul};
        }

        .vd-insight strong {
          color: ${colores.texto};
        }

        @media (min-width: 1500px) {
          .vd-stats {
            grid-template-columns: repeat(6, minmax(0, 1fr));
          }
        }

        @media (max-width: 1100px) {
          .vd-dashboard {
            padding: 18px;
          }

          .vd-stats {
            grid-template-columns: repeat(2, minmax(0, 1fr));
          }

          .vd-welcome-grid {
            grid-template-columns: 1fr;
          }
        }

        @media (max-width: 700px) {
          .vd-dashboard {
            padding: 13px;
          }

          .vd-hero {
            min-height: 0;
            padding: 24px 21px;
            border-radius: 20px;
          }

          .vd-hero-date {
            display: none;
          }

          .vd-hero h1 {
            font-size: 25px;
          }

          .vd-hero p {
            font-size: 13px;
          }

          .vd-stats {
            grid-template-columns: repeat(2, minmax(0, 1fr));
            gap: 10px;
          }

          .vd-stat {
            padding: 14px;
            border-radius: 16px;
          }

          .vd-stat-icon {
            width: 32px;
            height: 32px;
            font-size: 18px;
          }

          .vd-stat-top {
            align-items: flex-start;
            gap: 8px;
          }

          .vd-stat-title {
            font-size: 11px;
          }

          .vd-stat-value {
            margin-top: 13px;
            font-size: 24px;
          }

          .vd-welcome-card,
          .vd-panel {
            padding: 17px;
            border-radius: 17px;
          }

          .vd-charts-grid,
          .vd-insights {
            grid-template-columns: 1fr;
          }

          .vd-chart {
            height: 225px;
          }
        }

        @media (prefers-reduced-motion: reduce) {
          .vd-stat {
            transition: none;
          }
        }
      `}</style>

      {/* Encabezado principal */}
      <header className="vd-hero">
        <div className="vd-hero-content">
          <div className="vd-hero-eyebrow">
            <span>✧</span>
            Tu jornada en VexaDent
          </div>

          <h1>Todo tu consultorio, en un solo lugar.</h1>

          <p>
            Consulta rápidamente lo que requiere tu atención durante el día.
          </p>
        </div>

        <div className="vd-hero-date">
          <div className="vd-hero-date-label">Hoy es</div>
          <div className="vd-hero-date-value">{fechaLarga}</div>
        </div>
      </header>

      {/* Indicadores principales */}
      <section className="vd-section">
        <div className="vd-section-heading">
          <div>
            <h2>Resumen de hoy</h2>
            <p>Los indicadores principales de tu consultorio.</p>
          </div>
          <span className="vd-section-tag">VexaDent</span>
        </div>

        <div className="vd-stats">
          <TarjetaIndicador
            titulo="Citas hoy"
            valor={citasHoy.length}
            descripcion="Citas programadas"
            icono={iconos.citas}
            color="#2563eb"
            fondoIcono="#eff6ff"
          />

          <TarjetaIndicador
            titulo="Ingresos hoy"
            valor={formatoDinero(ingresosHoy)}
            descripcion="Pagos registrados hoy"
            icono={iconos.ingresos}
            color="#16a34a"
            fondoIcono="#dcfce7"
          />

          <TarjetaIndicador
            titulo="Pacientes nuevos"
            valor={pacientesMes}
            descripcion="Registros de este mes"
            icono={iconos.pacientes}
            color="#9333ea"
            fondoIcono="#f3e8ff"
          />

          <TarjetaIndicador
            titulo="WhatsApp"
            valor={whatsappUnread}
            descripcion="Mensajes pendientes"
            icono={iconos.whatsapp}
            color="#16a34a"
            fondoIcono="#dcfce7"
          />

          <TarjetaIndicador
            titulo="Saldo pendiente"
            valor={
              cuentasPorCobrar === null
                ? "..."
                : formatoDinero(saldoPendiente)
            }
            descripcion="Total por cobrar"
            icono={iconos.saldo}
            color="#ea580c"
            fondoIcono="#ffedd5"
          />

          <TarjetaIndicador
            titulo="Ingresos del mes"
            valor={formatoDinero(ingresosMes)}
            descripcion="Registrados este mes"
            icono="↗"
            color="#0d9488"
            fondoIcono="#ccfbf1"
          />
        </div>
      </section>

      {/* Bienvenida y próxima cita */}
      <section className="vd-section">
        <div className="vd-welcome-grid">
          <div className="vd-welcome-card">
            <div className="vd-welcome-icon">✧</div>

            <div>
              <h3>
                {saludo},{" "}
                {currentUser?.nombre || "Usuario"}
              </h3>

              <p>
                Hoy tienes {citasHoy.length} citas programadas,{" "}
                {pacientesMes} pacientes nuevos este mes y{" "}
                {formatoDinero(ingresosHoy)} MXN registrados.
              </p>

              <p style={{ marginTop: 8 }}>
                {proximaCita
                  ? `Tu próxima cita es a las ${proximaCita.hora}.`
                  : "No tienes más citas programadas para hoy."}
              </p>
            </div>
          </div>

          <div className="vd-welcome-card">
            <div className="vd-welcome-icon">▦</div>

            <div>
              <h3>Próxima cita</h3>

              {proximaCita ? (
                <>
                  <div className="vd-appointment-time">
                    {proximaCita.hora}
                  </div>

                  <div className="vd-appointment-name">
                    {proximaCita.pacienteNombre ||
                      proximaCita.motivo}
                  </div>
                </>
              ) : (
                <div className="vd-appointment-empty">
                  Sin citas pendientes para hoy.
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Resumen mensual */}
      <section className="vd-section">
        <Panel
          titulo="Resumen mensual"
          subtitulo="Actividad registrada durante el mes actual."
        >
          <div className="vd-stats" style={{ gridTemplateColumns: "repeat(2, minmax(0, 1fr))" }}>
            <div
              style={{
                padding: 18,
                borderRadius: 15,
                background: "#f5f3ff",
              }}
            >
              <div style={{ color: colores.secundario, fontSize: 12 }}>
                Pacientes nuevos
              </div>

              <div
                style={{
                  marginTop: 8,
                  fontSize: 27,
                  fontWeight: 750,
                  color: colores.texto,
                }}
              >
                {pacientesMes}
              </div>

              <div style={{ marginTop: 5, fontSize: 11, color: colores.secundario }}>
                Registros de este mes
              </div>
            </div>

            <div
              style={{
                padding: 18,
                borderRadius: 15,
                background: "#ecfdf5",
              }}
            >
              <div style={{ color: colores.secundario, fontSize: 12 }}>
                Ingresos registrados
              </div>

              <div
                style={{
                  marginTop: 8,
                  fontSize: 27,
                  fontWeight: 750,
                  color: "#15803d",
                  overflowWrap: "anywhere",
                }}
              >
                {formatoDinero(ingresosMes)}
              </div>

              <div style={{ marginTop: 5, fontSize: 11, color: colores.secundario }}>
                Total del mes actual
              </div>
            </div>
          </div>
        </Panel>
      </section>

      {/* Gráficas */}
      <section className="vd-section">
        <div className="vd-section-heading">
          <div>
            <h2>Actividad del consultorio</h2>
            <p>Visualiza la evolución de tus indicadores.</p>
          </div>
        </div>

        <div className="vd-charts-grid">
          <Panel
            titulo="Ingresos últimos 7 días"
            subtitulo="Pagos registrados por día."
          >
            <div className="vd-chart">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart
                  data={ingresos7Dias}
                  margin={{ top: 8, right: 8, left: 0, bottom: 0 }}
                >
                  <CartesianGrid
                    strokeDasharray="3 3"
                    stroke="#e9eef6"
                    vertical={false}
                  />

                  <XAxis
                    dataKey="dia"
                    tick={{ fill: "#8190a8", fontSize: 11 }}
                    axisLine={false}
                    tickLine={false}
                  />

                  <YAxis
                    tick={{ fill: "#8190a8", fontSize: 11 }}
                    axisLine={false}
                    tickLine={false}
                    width={48}
                  />

                  <Tooltip
                    formatter={(value) => [
                      formatoDinero(value),
                      "Ingresos",
                    ]}
                    contentStyle={{
                      borderRadius: 12,
                      border: "1px solid #e7edf5",
                      boxShadow: "0 8px 24px rgba(15,23,42,.08)",
                    }}
                  />

                  <Line
                    type="monotone"
                    dataKey="total"
                    name="Ingresos"
                    stroke={colores.azul}
                    strokeWidth={3}
                    dot={{
                      r: 3,
                      fill: colores.azul,
                      strokeWidth: 2,
                      stroke: "#ffffff",
                    }}
                    activeDot={{ r: 5 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </Panel>

          <Panel
            titulo="Pacientes últimos 30 días"
            subtitulo="Nuevos registros por fecha."
          >
            <div className="vd-chart">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={pacientes30Dias}
                  margin={{ top: 8, right: 8, left: 0, bottom: 0 }}
                >
                  <CartesianGrid
                    strokeDasharray="3 3"
                    stroke="#e9eef6"
                    vertical={false}
                  />

                  <XAxis
                    dataKey="dia"
                    interval={4}
                    tick={{ fill: "#8190a8", fontSize: 10 }}
                    axisLine={false}
                    tickLine={false}
                  />

                  <YAxis
                    allowDecimals={false}
                    tickCount={5}
                    tick={{ fill: "#8190a8", fontSize: 11 }}
                    axisLine={false}
                    tickLine={false}
                    width={30}
                  />

                  <Tooltip
                    formatter={(value) => [value, "Pacientes"]}
                    labelFormatter={(label) => `Día ${label}`}
                    contentStyle={{
                      borderRadius: 12,
                      border: "1px solid #e7edf5",
                      boxShadow: "0 8px 24px rgba(15,23,42,.08)",
                    }}
                  />

                  <Bar
                    dataKey="total"
                    name="Pacientes"
                    fill="#16a34a"
                    radius={[5, 5, 0, 0]}
                    maxBarSize={18}
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </Panel>

          <Panel
            titulo="Ocupación semanal"
            subtitulo="Porcentaje estimado según las citas registradas."
          >
            <div className="vd-chart">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={ocupacion}
                  margin={{ top: 8, right: 8, left: 0, bottom: 0 }}
                >
                  <CartesianGrid
                    strokeDasharray="3 3"
                    stroke="#e9eef6"
                    vertical={false}
                  />

                  <XAxis
                    dataKey="dia"
                    tick={{ fill: "#8190a8", fontSize: 11 }}
                    axisLine={false}
                    tickLine={false}
                  />

                  <YAxis
                    domain={[0, 100]}
                    tickFormatter={(value) => `${value}%`}
                    tick={{ fill: "#8190a8", fontSize: 11 }}
                    axisLine={false}
                    tickLine={false}
                    width={42}
                  />

                  <Tooltip
                    formatter={(value) => [`${value}%`, "Ocupación"]}
                    contentStyle={{
                      borderRadius: 12,
                      border: "1px solid #e7edf5",
                      boxShadow: "0 8px 24px rgba(15,23,42,.08)",
                    }}
                  />

                  <Bar
                    dataKey="porcentaje"
                    name="Ocupación"
                    fill="#f59e0b"
                    radius={[5, 5, 0, 0]}
                    maxBarSize={32}
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </Panel>
        </div>
      </section>

      {/* Insights: se conservan los cinco avisos originales */}
      <section className="vd-section">
        <Panel
          titulo="Insights"
          subtitulo="Resumen rápido de los puntos importantes de hoy."
        >
          <div className="vd-insights">
            <div className="vd-insight">
              <span className="vd-insight-dot" />
              <span>
                Hoy tienes <strong>{citasHoy.length}</strong> citas programadas.
              </span>
            </div>

            <div className="vd-insight">
              <span className="vd-insight-dot" style={{ background: "#16a34a" }} />
              <span>
                Existen <strong>{whatsappUnread}</strong> mensajes sin leer.
              </span>
            </div>

            <div className="vd-insight">
              <span className="vd-insight-dot" style={{ background: "#9333ea" }} />
              <span>
                Tienes <strong>{pacientesMes}</strong> pacientes nuevos este mes.
              </span>
            </div>

            <div className="vd-insight">
              <span className="vd-insight-dot" style={{ background: "#0d9488" }} />
              <span>
                Tus ingresos del mes son <strong>{formatoDinero(ingresosMes)}</strong>.
              </span>
            </div>

            <div className="vd-insight">
              <span className="vd-insight-dot" style={{ background: "#ea580c" }} />
              <span>
                Hay <strong>{formatoDinero(saldoPendiente)}</strong> pendientes por cobrar.
              </span>
            </div>
          </div>
        </Panel>
      </section>
    </div>
  );
}
